<template>
	<ConfigProvider :locale="antdLocale" :direction="direction">
		<Layout class="layout">
			<CompactMenu
				:selectedKey="selectedMenuName"
				:items="menuItems"
				@select="onMenuChange"
			/>
			<TopOverlay v-if="moduleStatusesVisible" />
			<LayoutContent class="main-container">
				<MapContainer />
				<div :class="wrapperClasses" ref="wrapper" tabindex="0">
					<router-view :key="routerKey"></router-view>
				</div>
			</LayoutContent>
		</Layout>
	</ConfigProvider>
</template>

<script>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { ConfigProvider, Layout, LayoutContent } from 'ant-design-vue';
import { useRouter, useRoute } from 'vue-router';

import dayjs from 'dayjs';

import { getLocaleData } from '@/locales/locales';

import { useUserStore } from '@/stores/user';
import { useSessionStore } from '@/stores/session';
import { useRoversHistoryStore } from '@/stores/roversHistoryStore';

import ApiOps from '@/api/ApiOps';
import MapConsts from '@/components/map/consts/MapConsts';

import CompactMenu from '@/components/common/CompactMenu';
import { useReactiveTitle } from '@/components/common/useReactiveTitle';
import TopOverlay from '@/components/floaters/TopOverlay';
import MapContainer from '@/components/top_level/MapContainer';

import { useProvideInjectWrapper } from '@/utils/useProvideInject';
import { useKeyboardMenuNavigation } from '@/utils/useKeyboardMenuNavigation';
import { useLocaleData } from '@/locales/useLocaleData';

export default {
	components: {
		ConfigProvider,
		Layout, LayoutContent,
		CompactMenu, TopOverlay, MapContainer,
	},
	setup() {
		const wrapper = ref();
		const { provideValue } = useProvideInjectWrapper;
		provideValue(wrapper);

		const { localeData } = useLocaleData();
		watch(localeData, () => {
			dayjs.locale(localeData.value.locale);
			document.dir = localeData.value.direction;
		}, { immediate: true });
		const antdLocale = computed(() => getLocaleData(useSessionStore().locale).antdLocale);
		const direction = computed(() => getLocaleData(useSessionStore().locale).direction);

		const userStore = useUserStore();
		useKeyboardMenuNavigation();
		const menuItems = computed(() => userStore.menuItems);
		const moduleStatusesVisible = computed(() => userStore.allowToSeeModuleStatuses);
		const route = useRoute();
		const selectedMenuName = computed(() => {
			if (!route.name) {
				return 'home-map';
			}
			return route.name;
		});
		// stats-type / settings-type: key by name so tab param changes (:id, :subId) do not
		// remount CombinedPage (Ant Tabs ink would animate from first tab every time).
		// Other routes: key by path so e.g. base-stations-edit remounts when :id changes.
		const routerKey = computed(() => {
			const name = route.name;
			if (name === 'stats-type' || name === 'settings-type') {
				return name;
			}
			return route.path;
		});

		useReactiveTitle();

		const fixContentScroll = () => {
			// контент у нас не в body, а в wrapper
			// поэтому нужно фокусироваться на wrapper, чтобы сразу был скролл кнопками
			nextTick(() => {
				wrapper.value?.focus();
			});
		};

		const router = useRouter();
		const onMenuChange = (itemKey) => {
			if (itemKey === 'logout') {
				ApiOps.logout();
				router.push({ name: 'home-map' });
			} else {
				router.push({ name: itemKey });
			}
			fixContentScroll();
		};
		const roversHistoryStore = useRoversHistoryStore();
		const wrapperClasses = computed(() => ({
			'router-view-wrapper': true,
			'router-view-wrapper-ignore-mouse': MapConsts.isMapFocusedRoute(route.name),
			'router-view-wrapper-narrow': MapConsts.isMapPartialRoute(route.name) && roversHistoryStore.showOnMap,
		}))
		onMounted(() => {
			ApiOps.profileGetDetails();
			fixContentScroll();
		});
		return {
			wrapper,
			wrapperClasses,
			antdLocale,
			direction,
			menuItems,
			moduleStatusesVisible,
			selectedMenuName,
			routerKey,
			onMenuChange,
		}
	},
}
</script>

<style>
#app {
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	height: 100%;
}

section.ant-layout {
	height: 100%;
}

.router-view-wrapper {
	z-index: 3;
	position: absolute;
	overflow: auto;
	inset: 0;
	background-color: rgba(0, 0, 0, 0.4);
}

.router-view-wrapper-ignore-mouse {
	pointer-events: none;
	background-color: transparent;
}

.router-view-wrapper-narrow {
	width: 50%;
	background-color: transparent;
}
</style>
